import { Global } from '~/global'
import { Moment } from 'moment'
import { CheckInsSummaryType, CustomerType, EmployeeType } from '~/generated/types'
import { CheckInsSummary, Customer, Employee, Maybe } from '~/app/types'
import { asCheckIn } from '~/react/employee-check-in/EmployeeCheckInSortUtil'
import * as OWUtils from '~/utils/OWUtils'
import * as Constants from '~/utils/Constants'

export const asEmployee = (e?: Maybe<Partial<EmployeeType>>) => {
    return {
        id: e?.employeeId,
        first_name: e?.firstName,
        last_name: e?.lastName,
        public_id: e?.publicId,
        full_name: e?.fullName,
        nick_name: e?.nickName,
        title: e?.title,
        email: e?.email,
        profile_photo: e?.profilePhoto,
        profile_photo_thumbnail: e?.profilePhotoThumbnail,
        is_active: e?.isActive,
        employment_duration: {
            years: e?.employmentDuration?.years,
            months: e?.employmentDuration?.months,
            human_friendly: e?.employmentDuration?.humanFriendly,
        },
    } as Employee
}

export const asEmployeeType = (e?: Maybe<Partial<Employee>>) => {
    return {
        employeeId: e?.id,
        fullName: e?.full_name,
        title: e?.title,
    } as EmployeeType
}

export const asCheckInsSummary = (s?: Maybe<Partial<CheckInsSummaryType>>) => {
    return {
        employee: {
            public_id: s?.employee?.publicId,
            full_name: s?.employee?.fullName,
            title: s?.employee?.title,
            direct_reports_count: s?.employee?.directReportsCount,
            descendants_count: s?.employee?.descendantsCount,
            profile_photo_thumbnail: s?.employee?.profilePhotoThumbnail,
        },
        lastCheckIn: s?.lastCheckIn && asCheckIn(s.lastCheckIn),
        pending: s?.pending,
        overdue: s?.overdue,
        veryOverdue: s?.veryOverdue,
        statusGreen: s?.statusGreen,
        statusYellow: s?.statusYellow,
        statusRed: s?.statusRed,
        engagementGreen: s?.engagementGreen,
        engagementYellow: s?.engagementYellow,
        engagementRed: s?.engagementRed,
    } as CheckInsSummary
}

export const asCustomer = (c?: any) => {
    return {
        id: Number(c?.id),
        name: c?.name,
        logo: c?.logo,
        is_prospect: c?.isProspect,
        is_active: c?.isActive,
        vc_id: c?.vcId,
        appstats_id: c?.appstatsId,
        member_count: c?.memberCount,
    } as Customer
}

export const asCustomerType = (c?: any) => {
    return { customerId: c?.id, name: c?.name, logo: c?.logo, aliases: c?.aliases ?? [] } as CustomerType
}

export const showAnniversaryBalloons = (start: Moment, today: Moment) => {
    const startMoment = start.clone()
    const nowMoment = today.clone()

    const thisYear = startMoment.year() === nowMoment.year()

    if (startMoment.format('MM-DD') === '02-29' && !nowMoment.isLeapYear()) {
        startMoment.add(-1, 'day')
    }

    const cubeCelebrationEnabled = OWUtils.hasFeature(Constants.Features.CUBE_CELEBRATIONS_ENABLED)
    const cubeYear = !thisYear && (nowMoment.year() - startMoment.year()) % 5 === 0

    // Used to calculate if within 7-day window for cube celebration
    const defaultYear = nowMoment.year()
    const adjustedStartMoment = startMoment.clone().year(defaultYear)
    const adjustedNowMoment = nowMoment.clone().year(defaultYear)
    const dayDifference = adjustedNowMoment.diff(adjustedStartMoment, 'days')
    const within7DayWindow = dayDifference >= 0 && dayDifference < 7

    let anniversaryDay = !thisYear && startMoment.format('MM-DD') === nowMoment.format('MM-DD')

    if (!thisYear && (!cubeYear || !cubeCelebrationEnabled)) {
        const weekdayAdjustedStart = startMoment.clone()
        // the anniversary falls on a sunday, so adjust the start date to compare to the following monday
        if (adjustedStartMoment.day() === 0) {
            weekdayAdjustedStart.add(1, 'day')
        }
        // the anniversary falls on a saturday, so adjust the start date to compare to the following monday
        if (adjustedStartMoment.day() === 6) {
            weekdayAdjustedStart.add(2, 'day')
        }
        anniversaryDay = weekdayAdjustedStart.format('MM-DD') === nowMoment.format('MM-DD')
    }

    return (
        Global.PROFILE_CELEBRATIONS_ENABLED &&
        (anniversaryDay || (cubeYear && within7DayWindow && cubeCelebrationEnabled))
    )
}
